import { formatPhoneDisplay, type CallSource } from '@brimo/shared';
import type { CallView } from './api';

/**
 * The softphone's local state machine (one call per tab): idle → dialing („wählt …", ringing inside it)
 * → connected (timer from the first `connected` event) → ended | failed. The server keeps the authoritative
 * call row; every transition is reported with `POST /calls/:id/events`.
 */

export type CallPhase = 'idle' | 'dialing' | 'connected' | 'ended' | 'failed';

/** A ringing inbound call waiting for „Annehmen" / „Ablehnen" (docs/dev/api-dialer.md „Inbound calls"). */
export interface IncomingCallInfo {
  callId: string;
  contactId: string;
  /** Null for a caller the server created for this call. */
  contactName: string | null;
  /** E.164 of the caller. */
  from: string;
}

export interface CallSnapshot {
  phase: CallPhase;
  callId: string | null;
  contactId: string | null;
  source: CallSource | null;
  sessionId: string | null;
  /** Who called: the browser (`outbound`) or the contact (`inbound`, accepted from the „Eingehender Anruf" dialog). */
  direction: 'inbound' | 'outbound' | null;
  /** The inbound call ringing right now; independent of the phase. */
  incoming: IncomingCallInfo | null;
  /** The callee's phone is ringing (Twilio `ringing` with answerOnBridge). */
  ringing: boolean;
  /** Local clock (ms) of the first `connected` event. */
  connectedAt: number | null;
  /** Local clock (ms) of the end. */
  endedAt: number | null;
  muted: boolean;
  /** Banner text: an API refusal, the microphone, a failed connection. */
  error: string | null;
  /** The server's call row after the last reported event. */
  view: CallView | null;
  localStream: MediaStream | null;
  remoteStream: MediaStream | null;
  /** The voice client finished `ready()` (token + device) at least once in this tab. */
  deviceReady: boolean;
}

export const IDLE_CALL: CallSnapshot = {
  phase: 'idle',
  callId: null,
  contactId: null,
  source: null,
  sessionId: null,
  direction: null,
  incoming: null,
  ringing: false,
  connectedAt: null,
  endedAt: null,
  muted: false,
  error: null,
  view: null,
  localStream: null,
  remoteStream: null,
  deviceReady: false,
};

export function isLive(phase: CallPhase): boolean {
  return phase === 'dialing' || phase === 'connected';
}

/** The running clock of a call, „mm:ss" (hours roll into the minutes: 64:05). */
export function formatCallTimer(milliseconds: number): string {
  const total = Math.max(0, Math.floor(milliseconds / 1000));
  const minutes = Math.floor(total / 60);
  const seconds = total % 60;
  return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
}

/** Seconds between the connection and the end (or `now`); null before a connection. */
export function elapsedSeconds(snapshot: CallSnapshot, now: number): number | null {
  if (snapshot.connectedAt === null) return null;
  const end = snapshot.endedAt ?? now;
  return Math.max(0, Math.round((end - snapshot.connectedAt) / 1000));
}

/** The caller line of „Eingehender Anruf": the contact's name with its number, or only the number of a new caller. */
export function incomingCallerLabel(info: IncomingCallInfo): { name: string; number: string | null } {
  const number = info.from === '' ? null : formatPhoneDisplay(info.from);
  if (info.contactName !== null && info.contactName !== '') return { name: info.contactName, number };
  return { name: number ?? 'Unbekannt', number: null };
}
